import { Stack } from '@mui/material'
import { FormTextField, tokens } from 'ComponentLibrary'
import { useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'
import { useEffect, useMemo } from 'react'
import { Task } from 'ComponentLibrary/src/common/tasks'
import FormFooterButtons from 'ComponentLibrary/src/molecules/Form/FormFooter/FormFooterButtons'
import { Toggle } from 'ComponentLibrary/src/atoms/Toggle/Toggle'

export type FormTaskTitleProps = {
    task: Pick<Task, 'title' | 'status'>
    isMutatingTask?: boolean
    onSubmit: (updatedTask: Pick<Task, 'title'>, onError?: () => void) => void
    onToggleStatus: () => void
    onCancel: () => void
    copy?: Partial<FormTaskTitleCopy>
}

type TaskForm = Pick<Task, 'title'>

export type FormTaskTitleCopy = {
    updateErrorMessage: string
    cancelAriaLabel: string
    submitAriaLabel: string
}

const DEFAULT_FORM_TASK_TITLE_COPY: FormTaskTitleCopy = {
    updateErrorMessage: 'Es ist ein Fehler beim Senden der Änderungen aufgetreten. Bitte erneut versuchen.',
    cancelAriaLabel: 'Cancel',
    submitAriaLabel: 'Submit',
}

export function FormTaskTitle({ task, isMutatingTask = false, onSubmit, onCancel, onToggleStatus, copy }: FormTaskTitleProps) {
    const resolvedCopy = { ...DEFAULT_FORM_TASK_TITLE_COPY, ...copy }
    const formValues = useMemo(() => {
        return {
            title: task.title,
        }
    }, [task.title])

    const {
        control,
        formState: { errors, dirtyFields },
        handleSubmit,
        reset,
        setError,
    } = useForm<TaskForm>({
        defaultValues: formValues,
    })

    useEffect(
        function updateDefaultValues() {
            reset(formValues)
        },
        [formValues, reset]
    )

    const handleCancel = () => {
        reset()
        onCancel()
    }

    const submitForm = (data: TaskForm) => {
        onSubmit(
            {
                ...data,
            },
            () => {
                setError('root', {
                    type: 'server',
                    message: resolvedCopy.updateErrorMessage,
                })
            }
        )
    }

    return (
        <Stack direction="row" width="100%" alignItems="center" gap={tokens.SizingSpacing100}>
            <Toggle isOn={task.status === 'done'} onToggle={onToggleStatus} />
            <FormTextField
                textAlign="left"
                gridStyles={{ width: '100%' }}
                size="small"
                required
                control={control}
                errors={errors}
                fieldName="title"
            />
            <FormFooterButtons
                onSubmit={handleSubmit(submitForm)}
                onCancel={handleCancel}
                isPersisting={isMutatingTask}
                shouldDisableSubmit={isEmpty(dirtyFields)}
                copy={{
                    cancelAriaLabel: resolvedCopy.cancelAriaLabel,
                    submitAriaLabel: resolvedCopy.submitAriaLabel,
                }}
            />
        </Stack>
    )
}
